import { createContext, useContext, useCallback, useState, useMemo } from 'react';
import uniqueId from 'lodash/uniqueId';

const ModalManagerContext = createContext(null);

// wrapper for the provider
export const ModalManagerProvider = ({ children }) => {
  const [modals, setModals] = useState({});

  const openModal = useCallback((key, component, mountingGroup, props = {}) => {
    setModals(current => ({
      ...current,
      [key]: {
        component,
        mountingGroup,
        props,
      },
    }));
  }, []);

  const updateModal = useCallback((key, propsChange) => {
    setModals(current => {
      if (!current[key]) {
        return current;
      }
      return {
        ...current,
        [key]: {
          ...current[key],
          props: {
            ...current[key].props,
            ...propsChange,
          },
        },
      };
    });
  }, []);

  const closeModal = useCallback((key) => {
    return new Promise((resolve) => {
      setModals(current => {
        if (!current[key]) {
          resolve();
          return current;
        }
        const modals = { ...current };
        delete modals[key];
        setTimeout(() => {
          resolve();
        }, 300);
        return modals;
      });
    });
  }, []);

  const getModalFunctions = useCallback((modalComponent, mountingGroup) => {
    const key = uniqueId('modal_');
    const open = (props) => openModal(key, modalComponent, mountingGroup, props);
    const update = (props) => updateModal(key, props);
    const close = () => closeModal(key);
    return [open, close, update];
  }, [openModal, closeModal, updateModal]);

  const value = useMemo(() => ({
    getModalFunctions,
    modals,
    closeModal,
  }), [getModalFunctions, modals, closeModal]);

  return (
    <ModalManagerContext.Provider value={value}>
      {children}
    </ModalManagerContext.Provider>
  );
};

// custom hook
export const useModalManager = () => useContext(ModalManagerContext);
